import { InputAdornment } from "@mui/material";
import moment from "moment-timezone";
import {
  CreateInternalExpenseMutation,
  INTERNAL_EXPENSE_CATEGORY_LABEL,
  INTERNAL_EXPENSE_CATEGORY_OPTIONS,
  InternalExpenseEditQuery,
  UpdateInternalExpenseMutation,
  type UpdateInternalExpenseInput,
} from "@/admin/entities/internalExpenses";
import type {
  CreateSchemaDefinition,
  EditSchemaDefinition,
} from "@/shared/components/CRUD/types";
import type { Schema } from "@/shared/components/Form/types";
import {
  InternalExpenseCategoryType,
  type InternalExpenseEditQueryType,
} from "@/types/graphql/admin.generated";

export { INTERNAL_EXPENSE_CATEGORY_OPTIONS, INTERNAL_EXPENSE_CATEGORY_LABEL };

export const filterSchema: Schema = {
  search: {
    label: "Поиск",
    placeholder: "Название, поставщик или комментарий",
    type: "text",
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
  },
  category: {
    label: "Категория",
    type: "select",
    options: [
      { label: "Все", value: "" },
      ...INTERNAL_EXPENSE_CATEGORY_OPTIONS,
    ],
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
  },
  dateFrom: {
    label: "Потрачено от",
    type: "date",
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
  },
  dateTo: {
    label: "Потрачено до",
    type: "date",
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
  },
  withDeleted: {
    label: "Показывать удалённые",
    type: "checkbox",
    xs: 12,
    fullWidth: true,
    priority: "secondary",
  },
};

export function normalizeFilter(
  filter: Record<string, unknown>
): Record<string, unknown> {
  const normalized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value === "" || value === null || value === undefined) continue;

    // В форме выбирается календарная дата. Начало/конец дня гарантируют,
    // что месячный диапазон включает первый и последний день целиком.
    if (key === "dateFrom" || key === "dateTo") {
      const date = moment(value as moment.MomentInput);
      normalized[key] = (
        key === "dateFrom" ? date.startOf("day") : date.endOf("day")
      ).toISOString();
      continue;
    }

    normalized[key] = value;
  }
  return normalized;
}

const expenseSchema: Schema = {
  spentAt: {
    label: "Дата расхода",
    type: "date",
    required: true,
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  category: {
    label: "Категория",
    type: "select",
    required: true,
    options: [...INTERNAL_EXPENSE_CATEGORY_OPTIONS],
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  title: {
    label: "На что потратили",
    type: "text",
    required: true,
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  vendor: {
    label: "Поставщик",
    helperText: "Например Google Cloud, Cloudflare или подрядчик",
    type: "text",
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  amount: {
    label: "Сумма",
    helperText: "Все внутренние расходы ведём только в USD",
    type: "number",
    step: 0.01,
    required: true,
    startAdornment: <InputAdornment position="start">$</InputAdornment>,
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  note: {
    label: "Комментарий",
    type: "markdown",
    xs: 12,
  },
};

export const createSchema: CreateSchemaDefinition = {
  main: {
    label: "Новый расход",
    schema: expenseSchema,
    createMutation: CreateInternalExpenseMutation,
  },
};

export const createInitialVariables = {
  spentAt: new Date().toISOString(),
  category: InternalExpenseCategoryType.InfrastructureType,
} as const;

export const editSchema: EditSchemaDefinition = {
  main: {
    label: "Расход",
    type: "form" as const,
    schema: expenseSchema,
    editQuery: InternalExpenseEditQuery,
    updateMutation: UpdateInternalExpenseMutation,
    getTitle: (data: unknown) => {
      const typed = data as InternalExpenseEditQueryType;
      return typed.internalExpense.title;
    },
    getEditData: (data: unknown): UpdateInternalExpenseInput => {
      const { internalExpense } = data as InternalExpenseEditQueryType;
      return {
        spentAt: internalExpense.spentAt,
        category: internalExpense.category,
        title: internalExpense.title,
        vendor: internalExpense.vendor ?? "",
        amount: internalExpense.amount.value,
        note: internalExpense.note ?? "",
      };
    },
  },
};
